import React from 'react';
import { Globe, Zap, Shield, Award } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const WhyChooseSection = () => {
  const features = [
    {
      icon: Globe,
      title: 'Global Aircraft Spare Parts Supply',
      description: 'Comprehensive sourcing network spanning major aviation markets worldwide'
    },
    {
      icon: Zap,
      title: 'Fast Sourcing and Logistics Support',
      description: 'Rapid procurement and delivery to minimize aircraft downtime'
    },
    {
      icon: Shield,
      title: 'Reliable Aviation Component Distribution',
      description: 'Trusted supply chain for critical aircraft components and systems'
    },
    {
      icon: Award,
      title: 'Quality Assurance and Certification',
      description: 'Rigorous verification processes ensuring compliance and authenticity'
    }
  ];

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-[#0B1C2C] relative overflow-hidden">
      {/* Subtle background glows */}
      <div className="absolute top-10 right-10 w-64 h-64 bg-[#D4AF37]/[0.06] rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#D4AF37]/[0.04] rounded-full blur-3xl"></div>

      {/* Hexagonal pattern */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='50' height='43.4' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M25 0l12.5 7.2v14.6L25 29 12.5 21.8V7.2z' fill='none' stroke='%23D4AF37' stroke-width='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '50px 43.4px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-14 sm:mb-16 space-y-3 sm:space-y-4" data-animate="fade-scale">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight">
            Why Choose <span className="text-[#D4AF37]" style={{textShadow: '0 0 30px rgba(212,175,55,0.3)'}}>Turbowing Aviation</span>
          </h2>
          <p className="text-base sm:text-lg text-white/70 max-w-3xl mx-auto px-4">
            Industry-leading aviation supply solutions backed by expertise and reliability
          </p>
        </div>        

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <Card
              key={index}
              data-animate="slide-up"
              data-tilt
              data-testid={`why-choose-card-${index}`}
              style={{ animationDelay: `${index * 0.1}s` }}
              className="group transition-all duration-500 hover:-translate-y-1.5 border border-white/10 backdrop-blur-xl bg-white/[0.04] overflow-hidden relative hover:bg-white/[0.07] hover:border-[#D4AF37]/40 hover:shadow-[0_8px_40px_rgba(212,175,55,0.15)] rounded-2xl"
            >
              <div className="absolute top-0 left-0 w-full h-0.5 bg-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_10px_rgba(212,175,55,0.5)]"></div>

              <CardContent className="p-6 sm:p-8 space-y-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-3.5 group-hover:bg-[#D4AF37]/20 group-hover:scale-105 transition-all duration-500">
                  <feature.icon className="w-full h-full text-[#D4AF37]" strokeWidth={1.5} />
                </div>

                <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-[#D4AF37] transition-colors duration-300 leading-snug">
                  {feature.title}
                </h3>

                <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
